<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useInsightsStore } from '../stores/insights-store'
import InsightsFilterBar from '../components/insights-filter-bar.vue'
import InsightsResultCard from '../components/insights-result-card.vue'
import InsightsResultTable from '../components/insights-result-table.vue'

const authStore = useAuthStore()
const insightsStore = useInsightsStore()
const router = useRouter()

// Restrict to site admins
if (!authStore.is_site_admin) {
  router.push({ name: 'dashboard' })
}

const isTableResult = computed(() => {
  return insightsStore.result?.value && Array.isArray(insightsStore.result.value)
})

const onApply = (params: Record<string, any>) => {
  insightsStore.fetchInsight(params)
}

const retry = () => {
  insightsStore.pollingError = null
}

const downloadCsv = () => {
  const items = insightsStore.result?.value
  if (!items?.length) return

  const columns = Object.keys(items[0]).filter((k) => k !== 'debug')
  const header = columns.join(',')
  const rows = items.map((item: Record<string, any>) =>
    columns
      .map((col) => {
        const val = item[col]
        if (val == null) return ''
        const str = String(val)
        return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str
      })
      .join(','),
  )
  const csv = [header, ...rows].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `insights-export-${new Date().toISOString().slice(0, 10)}.csv`
  link.click()
  URL.revokeObjectURL(url)
}
</script>

<template>
  <div v-if="authStore.is_site_admin" class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
    <div class="mb-8">
      <h1 class="text-2xl font-bold text-gray-900">Insights Explorer</h1>
      <p class="mt-1 text-sm text-gray-500">Select a metric to compute insights across your portfolio.</p>
    </div>

    <!-- Filter Bar -->
    <div class="mb-6 rounded-lg bg-white p-6 shadow">
      <InsightsFilterBar @apply="onApply" />
    </div>

    <!-- Error Banner -->
    <div v-if="insightsStore.pollingError" class="mb-6 rounded-md bg-red-50 p-4">
      <div class="flex">
        <div class="flex-1">
          <p class="text-sm text-red-700">{{ insightsStore.pollingError }}</p>
        </div>
        <button class="ml-3 text-sm font-medium text-red-700 hover:text-red-600" @click="retry">Dismiss</button>
      </div>
    </div>

    <!-- Results -->
    <template v-if="insightsStore.polling || insightsStore.result">
      <!-- Table display for array results -->
      <template v-if="isTableResult && !insightsStore.polling">
        <div class="mb-4 flex justify-end">
          <button
            class="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            @click="downloadCsv"
          >
            Download CSV
          </button>
        </div>
        <InsightsResultTable :items="insightsStore.result.value" />
      </template>

      <!-- Card display for scalar/object results -->
      <InsightsResultCard v-else :result="insightsStore.result" :skeleton="insightsStore.polling" />
    </template>

    <!-- Empty State -->
    <div v-else-if="!insightsStore.pollingError" class="rounded-lg bg-white p-12 text-center shadow">
      <p class="text-gray-500">Select a metric above to view insights.</p>
    </div>
  </div>
</template>
